import { useCallback, useContext, useEffect, useState } from "react";
import { CiSearch, CiCirclePlus } from "react-icons/ci";
import toast from "react-hot-toast";
import "../../styles/mobileUsers.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { getAvatarColor, getInitials } from "../../utils/utils";
import { MdDelete, MdEdit, MdLockOpen, MdLockReset, MdImage } from "react-icons/md";
import { LiaWalletSolid } from "react-icons/lia";
import AssignTemplateModal from "../../components/AssignTemplateModal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import ConfirmUnblockModal from "../../components/ConfirmUnblockModal";
import MobileUsersServices from "../../services/MobileUsersServices";
import AddMobileUserModal from "./modals/AddMobileUserModal";
import EditMobileUserModal from "./modals/EditMobileUserModal";
import AddMobileWalletAmountModal from "./modals/AddMobileWalletAmountModal";
import AdminUsersServices from "../../services/AdminUsersServices";
import { AuthContext } from "../../context/AuthContext";
import ResetPasswordModal from "../../components/ResetPasswordModal";
import PasswordServices from "../../services/PasswordServices";

const MobileUserManage = () => {
  const mobileApi = new MobileUsersServices();
  const adminSerApi = new AdminUsersServices();
  const passwordApi = new PasswordServices();

  const { user } = useContext(AuthContext);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileUserList, setMobileUserList] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchFilter, setSearchFilter] = useState("");
  const [pagination, setPagination] = useState(null);

  const [editMobileModal, setEditMobileModal] = useState(false);
  const [editMobileData, setEditMobileData] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [walletModal, setWalletModal] = useState(false);
  const [walletUser, setWalletUser] = useState(null);
  const [adminWalletBalance, setAdminWalletBalance] = useState(0.0);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [unblockModalOpen, setUnblockModalOpen] = useState(false);
  const [unblockUser, setUnblockUser] = useState(null);
  const [unblockLoading, setUnblockLoading] = useState(false);
  const [assignTplModal, setAssignTplModal] = useState(false);
  const [assignTplUser, setAssignTplUser] = useState(null);

  const getAdminWalletBalance = useCallback(async () => {
    try {
      const response = await adminSerApi.addAdminWalletBalanceById();

      if (response.isSuccess) {
        setAdminWalletBalance(response?.data?.wallet_balance);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      if (error?.response?.status === 403) return;
      toast.error("Failed to get admin wallet balance");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter]);

  const getAllMobileUserData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await mobileApi.getAllMobileUserData(
        page,
        limit,
        searchFilter,
      );
      if (response.isSuccess) {
        setMobileUserList(response.data || []);
        setPagination(response.pagination);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      if (error?.response?.status === 403) return;
      toast.error("Failed to load mobile users");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter]);

  useEffect(() => {
    if (user?.role_id !== 1) {
      getAdminWalletBalance();
    }
    getAllMobileUserData();
  }, [getAllMobileUserData]);

  const handleSearch = (e) => {
    setPage(1);
    setSearchFilter(e.target.value);
  };

  const toggleUserStatus = async (userData) => {
    const newStatus = userData.is_active === 1 ? 0 : 1;
    try {
      const response = await mobileApi.toggleMobileStatus({
        id: userData.id,
        is_active: newStatus,
      });
      if (response.isSuccess) {
        toast.success(response.message);
        setMobileUserList((prev) =>
          prev.map((u) =>
            u.id === userData.id ? { ...u, is_active: newStatus } : u,
          ),
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update user status",
      );
    }
  };

  const openEditModal = (userData) => {
    setEditMobileModal(true);
    setEditMobileData(userData);
  };

  const confirmDelete = (userData) => {
    setSelectedUser(userData);
    setDeleteModalOpen(true);
  };

  const openWalletModal = (userData) => {
    setWalletUser(userData);
    setWalletModal(true);
  };

  const openResetPasswordModal = (userData) => {
    setResetUser(userData);
    setResetModalOpen(true);
  };

  const openUnblockModal = (userData) => {
    setUnblockUser(userData);
    setUnblockModalOpen(true);
  };

  const openAssignTemplate = (userData) => {
    setAssignTplUser(userData);
    setAssignTplModal(true);
  };

  const closeAssignTemplate = () => {
    setAssignTplModal(false);
    setAssignTplUser(null);
  };

  const handleAssignTemplate = (payload) => mobileApi.assignTemplate(payload);

  const handleDelete = async () => {
    setDeleteLoading(true);
    mobileApi
      .deleteMobileUser(selectedUser.id)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          getAdminWalletBalance();
          getAllMobileUserData();
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error deleteMobileUser:", error);
        toast.dismiss();
        toast.error(
          error?.response?.data?.message || "Failed to delete mobile user",
        );
      })
      .finally(() => {
        setDeleteLoading(false);
        setDeleteModalOpen(false);
        setSelectedUser(null);
      });
  };

  const handleResetPassword = async (newPassword) => {
    if (!resetUser) return;
    setResetLoading(true);
    try {
      const response = await passwordApi.resetUserPassword({
        userId: resetUser.id,
        newPassword,
        description: `Mobile user password reset for user ${resetUser.username}`,
      });
      if (response.isSuccess) {
        toast.success(response.message || "Password reset successfully");
        setResetModalOpen(false);
        setResetUser(null);
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to reset password";
      toast.error(backendMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!unblockUser) return;
    setUnblockLoading(true);
    try {
      const response = await mobileApi.unblockMobileUser({ id: unblockUser.id });
      if (response.isSuccess) {
        toast.success(response.message || "User unblocked successfully");
        setMobileUserList((prev) =>
          prev.map((u) =>
            u.id === unblockUser.id ? { ...u, cancel_count: 0 } : u,
          ),
        );
        setUnblockModalOpen(false);
        setUnblockUser(null);
      } else {
        toast.error(response.message || "Failed to unblock user");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to unblock user",
      );
    } finally {
      setUnblockLoading(false);
    }
  };

  const Avatar = ({ name, image }) => {
    if (image) return <img src={image} alt={name} className="admin-avatar" />;
    const initials = getInitials(name);
    const bgColor = getAvatarColor(name);
    return (
      <div
        className="admin-avatar fallback-avatar"
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </div>
    );
  };

  return (
    <div className="mobile-user-manage-view">
      <div className="admin_user-header">
        <div className="admin-header-row">
          <div className="admin-search">
            <div className="search-box">
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search mobile user"
                value={searchFilter}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div>
            {user?.role_id !== 1 && (
              <div>Wallet balance : {adminWalletBalance}</div>
            )}
            <button className="add_admin-btn" onClick={() => setIsOpen(true)}>
              <CiCirclePlus size={18} />
              Add Mobile User
            </button>
          </div>
        </div>
      </div>

      <div>
        {loading ? (
          <Loader message="Loading Mobile Users..." />
        ) : mobileUserList.length === 0 ? (
          <NoData
            message="No Mobile Users Found"
            subText="You haven't added any mobile users yet. Click on Add Mobile User to get started."
          />
        ) : (
          <>
            <div className="admin-table-header">
              <div className="col-user">User</div>
              <div className="col-mobile">Mobile</div>
              <div className="col-status">Status</div>
              <div className="col-role">Created By</div>
              <div className="col-action">Action</div>
            </div>

            <div className="admin-table">
              {mobileUserList.map((user) => (
                <div className="admin-row" key={user.id}>
                  <div className="admin-user">
                    <Avatar name={user.name} image={user.profile_image} />
                    <div>
                      <div className="admin-name">{user.name}</div>
                      <div className="admin-username">({user.username})</div>
                      <div className="admin-username">
                        Wallet balance:{" "}
                        <span className="wallet">
                          {user?.wallet_balance ?? 0}
                        </span>
                      </div>
                      {user?.effective_template_name && (
                        <div
                          className={`admin-template-badge${
                            user.effective_template_source === "inherited"
                              ? " admin-template-badge--inherited"
                              : ""
                          }`}
                          title={
                            user.effective_template_source === "inherited"
                              ? `Inherited from ${user.effective_template_from?.name || user.effective_template_from?.username || "parent"} - assign one here to override`
                              : "Assigned to this user"
                          }
                        >
                          <MdImage size={14} />
                          <span>{user.effective_template_name}</span>
                          {user.effective_template_source === "inherited" && (
                            <small>via {user.effective_template_from?.username || "admin"}</small>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="admin-row-meta">
                    <div className="admin-col">
                      <div className="admin-username">{user.mobile}</div>
                    </div>

                    <div className="admin-col">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={user.is_active === 1}
                          onChange={() => toggleUserStatus(user)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                    <div className="admin-col">
                      <div className="admin-username">
                        {user.created_by_name || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="admin-actions">
                    {Number(user.cancel_count) >= 4 ? (
                      <>
                        <button
                          className="icon-btn unblock"
                          title="Unblock User"
                          onClick={() => openUnblockModal(user)}
                        >
                          <MdLockOpen size={18} /> Unblock
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="icon-btn edit"
                          title="Edit User"
                          onClick={() => openEditModal(user)}
                        >
                          <MdEdit size={18} />
                        </button>

                        <button
                          className="icon-btn delete"
                          title="Delete User"
                          onClick={() => confirmDelete(user)}
                        >
                          <MdDelete size={18} />
                        </button>

                        <button
                          className="icon-btn edit"
                          title="Reset Password"
                          onClick={() => openResetPasswordModal(user)}
                        >
                          <MdLockReset size={18} />
                        </button>

                        <button
                          className="icon-btn edit"
                          title="Add wallet balance"
                          onClick={() => openWalletModal(user)}
                        >
                          <LiaWalletSolid size={18} />
                        </button>
                        <button
                          className="icon-btn edit"
                          title={
                            user.template_id
                              ? "Change template"
                              : "Assign template"
                          }
                          onClick={() => openAssignTemplate(user)}
                        >
                          <MdImage size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                {/* Limit Selector */}
                <div className="mp-limit">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Navigation */}
                <div className="mp-controls">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>

                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </div>

                  <button
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>

                {/* Jump to Page */}
                <div className="mp-jump">
                  <select
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                  >
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AddMobileUserModal
        modalIsOpen={modalIsOpen}
        setIsOpen={setIsOpen}
        getAllMobileUserData={getAllMobileUserData}
      />

      <EditMobileUserModal
        editMobileModal={editMobileModal}
        setEditMobileModal={setEditMobileModal}
        editMobileData={editMobileData}
        setEditMobileData={setEditMobileData}
        getAllMobileUserData={getAllMobileUserData}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Mobile User"
        message={`Are you sure you want to delete \"${selectedUser?.name}\"?`}
        loading={deleteLoading}
      />

      <AddMobileWalletAmountModal
        walletModal={walletModal}
        setWalletModal={setWalletModal}
        walletUser={walletUser}
        setWalletUser={setWalletUser}
        setMobileUserList={setMobileUserList}
        adminWalletBalance={adminWalletBalance}
        setAdminWalletBalance={setAdminWalletBalance}
        role_id={user?.role_id}
      />

      <ResetPasswordModal
        show={resetModalOpen}
        onClose={() => {
          if (resetLoading) return;
          setResetModalOpen(false);
          setResetUser(null);
        }}
        onConfirm={handleResetPassword}
        userName={resetUser?.name}
        loading={resetLoading}
        title="Reset Mobile User Password"
      />

      <ConfirmUnblockModal
        show={unblockModalOpen}
        onClose={() => {
          if (unblockLoading) return;
          setUnblockModalOpen(false);
          setUnblockUser(null);
        }}
        onConfirm={handleUnblockUser}
        title="Unblock Mobile User"
        message={`Unblock "${unblockUser?.name}"? This will reset daily cancel count.`}
        loading={unblockLoading}
        confirmText="Yes, Unblock"
      />

      <AssignTemplateModal
        show={assignTplModal}
        onClose={closeAssignTemplate}
        targetUser={assignTplUser}
        onAssign={handleAssignTemplate}
        onSaved={getAllMobileUserData}
      />
    </div>
  );
};

export default MobileUserManage;
